import type { ConstantsNotificationChannelResponse } from "@gosaas/types";
import {
	formatNotificationChannelTypeLabel,
	NOTIFICATION_CHANNEL_TYPES_ALL,
	type NotificationChannelType,
} from "@gosaas/types/notification";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { IconPlug, IconPlus, IconSend, IconTrash } from "@tabler/icons-react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreateNotificationChannelDialog } from "@/components/dialogs/create-notification-channel";
import { DeleteNotificationChannelDialog } from "@/components/dialogs/delete-notification-channel";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { useFetchNotificationChannels } from "@/components/queries/notifications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function pathAfterRemovingSearchParam(
	pathname: string,
	search: string,
	paramName: string,
): string {
	const params = new URLSearchParams(search);
	params.delete(paramName);
	const q = params.toString();
	return `${pathname}${q ? `?${q}` : ""}`;
}

export function configSummary(c: ConstantsNotificationChannelResponse): string {
	const cfg = c.config ?? {};
	if (c.type === "webhook" && typeof cfg.url === "string") {
		return cfg.url;
	}
	if (c.type === "email" && typeof cfg.email === "string") {
		return cfg.email;
	}
	return "—";
}

export function typeLabel(type: string | undefined): string {
	if (!type) return "—";
	if ((NOTIFICATION_CHANNEL_TYPES_ALL as readonly string[]).includes(type)) {
		return formatNotificationChannelTypeLabel(type as NotificationChannelType);
	}
	return type;
}

export function DeleteChannelTargetDialog({
	deleteTarget,
	onClearTarget,
	onSuccess,
}: {
	deleteTarget: ConstantsNotificationChannelResponse | null;
	onClearTarget: () => void;
	onSuccess: () => void;
}) {
	if (!deleteTarget) {
		return null;
	}

	return (
		<DeleteNotificationChannelDialog
			open
			onOpenChange={(open) => {
				if (!open) onClearTarget();
			}}
			channel={deleteTarget}
			onSuccess={onSuccess}
		/>
	);
}

export function NotificationChannelsPage() {
	const router = useRouter();
	const { teamID, teams } = useTeam();
	const team = teams.find((t) => t.id === teamID);

	const canRead = hasPermission(
		team?.role as Role,
		"notifications:read" as Permission,
	);
	const canCreate = hasPermission(
		team?.role as Role,
		"notifications:create" as Permission,
	);
	const canUpdate = hasPermission(
		team?.role as Role,
		"notifications:update" as Permission,
	);
	const canDelete = hasPermission(
		team?.role as Role,
		"notifications:delete" as Permission,
	);

	const { channels, isLoading, refetchChannels } = useFetchNotificationChannels(
		teamID ?? "",
	);

	const [createOpen, setCreateOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] =
		useState<ConstantsNotificationChannelResponse | null>(null);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get("verified") === "true") {
			toast.success("Email address verified", {
				description: "You can attach this channel in notification rules.",
			});
			window.history.replaceState(
				{},
				"",
				pathAfterRemovingSearchParam(
					window.location.pathname,
					window.location.search,
					"verified",
				),
			);
		}
		if (params.get("error") === "invalid_token") {
			toast.error("Verification link is invalid or expired");
			window.history.replaceState(
				{},
				"",
				pathAfterRemovingSearchParam(
					window.location.pathname,
					window.location.search,
					"error",
				),
			);
		}
		if (params.get("error") === "server_error") {
			toast.error("Couldn't verify this channel", {
				description: "The verification link may be invalid or expired.",
			});
			window.history.replaceState(
				{},
				"",
				pathAfterRemovingSearchParam(
					window.location.pathname,
					window.location.search,
					"error",
				),
			);
		}
	}, []);

	useEffect(() => {
		if (canRead === false) {
			toast.warning("You do not have permission to view alert channels", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
		}
	}, [canRead, router]);

	const onTestWebhook = async (id: string) => {
		try {
			const res = await requestWithHandling<void>(() =>
				apiClient.api.teamNotificationsChannelsTestCreate(teamID as string, id),
			);
			if (res.code === "SUCCESS") {
				toast.success("Test webhook sent", {
					description: "The endpoint responded successfully.",
				});
				await refetchChannels();
			} else {
				toast.error("Channel test failed", { description: res.message });
			}
		} catch (e) {
			toast.error("Channel test failed", {
				description:
					e instanceof TypedClientApiError ? e.message : "Unknown error",
			});
		}
	};

	if (!teamID) return <Loader />;
	if (isLoading) return <Loader />;
	if (!canRead) return null;

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Notifications", href: "/team/notifications" },
					{ title: "Alert channels", href: null },
				]}
				title="Alert channels"
				description={
					<>
						Save webhooks and external inboxes once, then select them when you{" "}
						<Link
							to="/team/notifications/rules"
							className="text-primary underline-offset-4 hover:underline"
						>
							build notification rules
						</Link>
						.
					</>
				}
				icon={<IconPlug className="size-6 text-primary" aria-hidden />}
				action={
					canCreate && (
						<Button className="gap-2" onClick={() => setCreateOpen(true)}>
							<IconPlus className="h-4 w-4" />
							Add channel
						</Button>
					)
				}
			/>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Saved channels</CardTitle>
				</CardHeader>
				<CardContent>
					{!channels?.length ? (
						<p className="text-sm text-muted-foreground text-center py-8">
							No channels yet. Add a webhook or email destination, verify it,
							then attach it from the rule builder.
						</p>
					) : (
						<ul className="divide-y rounded-md border">
							{channels.map((c) => (
								<li
									key={c.id}
									className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 gap-y-3"
								>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="font-medium truncate">{c.name}</span>
											<Badge variant="secondary">{typeLabel(c.type)}</Badge>
											{c.type === "email" && !c.is_verified && (
												<Badge variant="destructive">
													Pending verification
												</Badge>
											)}
											{c.is_verified && (
												<Badge className="bg-green-500/15 text-green-700 dark:text-green-400">
													Verified
												</Badge>
											)}
										</div>
										<p className="text-xs text-muted-foreground truncate mt-1">
											{configSummary(c)}
										</p>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										{c.type === "webhook" && canUpdate && !c.is_verified && (
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="gap-1.5"
												onClick={() => c.id && onTestWebhook(c.id)}
											>
												<IconSend className="h-3.5 w-3.5" />
												Send test
											</Button>
										)}
										{canDelete && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="text-destructive hover:text-destructive"
												onClick={() => setDeleteTarget(c)}
												aria-label={`Delete ${c.name}`}
											>
												<IconTrash className="h-4 w-4" />
											</Button>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</CardContent>
			</Card>

			<CreateNotificationChannelDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onSuccess={refetchChannels}
			/>
			<DeleteChannelTargetDialog
				deleteTarget={deleteTarget}
				onClearTarget={() => setDeleteTarget(null)}
				onSuccess={refetchChannels}
			/>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute(
	"/_authorized/team/notifications/channels",
)({
	component: NotificationChannelsPage,
});
/* istanbul ignore end */
